
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Login from "./Login";
import { Navigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Index = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Log routing information to help troubleshoot
    console.log("Index component mounted");
    console.log("Authentication status:", isAuthenticated ? "authenticated" : "not authenticated");
    console.log("Auth loading status:", isLoading ? "loading" : "loaded");
    
    try {
      // Test localStorage access
      localStorage.setItem('stratafold_test', 'test');
      localStorage.removeItem('stratafold_test');
      console.log("Index: localStorage test successful");
    } catch (e) {
      console.error("Index: localStorage test failed", e);
      setError("Browser storage access is blocked. Please check your privacy settings.");
    }
    
    // Add a small delay to ensure we have authentication state loaded
    const timer = setTimeout(() => {
      setLocalLoading(false);
      console.log("Index: Local loading completed, rendering appropriate component");
    }, 1500); // Reduced from 2500ms for faster loading
    
    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoading]);

  // Show loading indicator while checking authentication
  if (isLoading || localLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
        <p className="text-gray-500">Loading application...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we check your authentication status</p>
      </div>
    );
  }

  // Show error if there was a problem
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Login />
        </div>
      </div>
    );
  }

  try {
    // Fallback to Login if anything is undefined
    if (isAuthenticated === undefined) {
      console.error("Index: isAuthenticated is undefined, falling back to Login");
      return <Login />;
    }
    
    // Redirect authenticated users to the project portfolio (projects-selector)
    console.log("Index: Rendering final component, isAuthenticated:", isAuthenticated);
    return isAuthenticated ? <Navigate to="/projects-selector" replace /> : <Login />;
  } catch (err) {
    console.error("Index: Error during render:", err);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error rendering page: {err instanceof Error ? err.message : 'Unknown error'}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Login />
        </div>
      </div>
    );
  }
};

export default Index;
